import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import AddAPhoto from '@mui/icons-material/AddAPhoto';
import { imageShape } from '../shapes/ImageShapes';
import { resizeAllFrames } from '../image_utilities/Resize';
import ImageForm from './ImageForm';

class ImagesTransform extends React.Component {
  state = { deleteDialogIsOpen: false, pendingDeleteName: '' };

  addImageButton = React.createRef();

  deleteWasConfirmed = false;

  findImageIndex = name => {
    return this.props.images.findIndex(image => image.name === name);
  };

  handleSubmit = (name, formData) => {
    if (this.props.disabled) return;
    this.props.onImageChangeStart();
    this.handlePositionInputSubmit(
      this.props.images[this.findImageIndex(name)],
      formData.x,
      formData.y
    )
      .then(modifiedPositionImage =>
        this.handleSizeInputSubmit(modifiedPositionImage, formData.width, formData.height)
      )
      .then(modifiedImage => this.props.onImageChange([{
        ...modifiedImage, playCount: formData.playCount ?? modifiedImage.playCount,
        endBehavior: formData.endBehavior ?? modifiedImage.endBehavior,
      }]))
      .catch(() => {
        // noop to stop loading
        this.props.onImageChange(this.props.images);
      });
  };

  handleDelete = name => {
    if (this.props.disabled) return;
    this.setState({ deleteDialogIsOpen: true, pendingDeleteName: name });
  };

  handleDeleteCancel = () => {
    this.setState({ deleteDialogIsOpen: false });
  };

  handleDeleteConfirm = () => {
    if (this.props.disabled || !this.state.deleteDialogIsOpen) return;
    const image = this.props.images[this.findImageIndex(this.state.pendingDeleteName)];
    this.setState({ deleteDialogIsOpen: false });
    if (!image) return;
    this.deleteWasConfirmed = true;
    this.props.onImageChangeStart();
    this.props.onImageRemoval([image]);
  };

  handleDeleteDialogExited = () => {
    if (!this.deleteWasConfirmed) return;
    this.deleteWasConfirmed = false;
    // The original trash button was removed. Wait for the dialog to finish
    // closing before focusing a control that survives even the final deletion.
    this.addImageButton.current?.focus();
  };

  handleSizeInputSubmit = (image, width, height) => {
    // Only resize if there's actually a reason to. Otherwise we'll just lower image quality for no reason
    if (image.frames[0].width !== width || image.frames[0].height !== height) {
      return resizeAllFrames(image.frames, width, height).then(resizedFrames => {
        return {
          ...image,
          frames: resizedFrames,
        };
      });
    } else {
      // Nothing to change, perform noop
      return new Promise(resolve => resolve(image));
    }
  };

  handlePositionInputSubmit = (image, x, y) => {
    return new Promise(resolve =>
      resolve({
        ...image,
        x,
        y,
      })
    );
  };

  render() {
    return (
      <>
        {this.props.disabled && (
          <Typography role="status" variant="body2" sx={{ mb: 2 }}>
            Processing images… You can edit again when this finishes.
          </Typography>
        )}
        <Box
          component="fieldset"
          aria-label="Image controls"
          aria-busy={this.props.disabled}
          disabled={this.props.disabled}
          sx={{ border: 0, p: 0, m: 0, minWidth: 0, opacity: this.props.disabled ? 0.6 : 1 }}
        >
          <Box mb={2}>
            <Button
              ref={this.addImageButton}
              className="glint-tactile"
              variant="contained"
              color="primary"
              fullWidth
              onClick={this.props.onAddImageClick}
              startIcon={<AddAPhoto />}
            >
              Add Image
            </Button>
          </Box>
          <Divider />
          {this.props.images.length ? (
            this.props.images.map((image, index) => (
              <React.Fragment key={image.name + index}>
                <ImageForm
                  image={image}
                  onSubmit={this.handleSubmit}
                  onDeleteClick={this.handleDelete}
                  style={{ animationDelay: `${index * 0.05}s` }}
                />
                <Divider />
              </React.Fragment>
            ))
          ) : (
            <Box mt={2}>
              <Typography>
                You will be able to manipulate image position and size here, but first you have to add
                one!
              </Typography>
            </Box>
          )}
        </Box>
        <Dialog
          open={this.state.deleteDialogIsOpen}
          onClose={this.handleDeleteCancel}
          slotProps={{ transition: { onExited: this.handleDeleteDialogExited } }}
          aria-labelledby="delete-image-title"
          aria-describedby="delete-image-description"
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle id="delete-image-title">Delete image?</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-image-description" sx={{ overflowWrap: 'anywhere' }}>
              Remove “{this.state.pendingDeleteName}” and all its frames from this project?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={this.handleDeleteCancel} sx={{ minHeight: 44 }}>
              Cancel
            </Button>
            <Button
              onClick={this.handleDeleteConfirm}
              disabled={this.props.disabled}
              variant="contained"
              color="error"
              sx={{ minHeight: 44 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

ImagesTransform.propTypes = {
  images: PropTypes.arrayOf(imageShape).isRequired,
  disabled: PropTypes.bool,
  onImageChange: PropTypes.func,
  onImageChangeStart: PropTypes.func,
  onImageRemoval: PropTypes.func,
  onAddImageClick: PropTypes.func,
};
ImagesTransform.defaultProps = {
  disabled: false,
  onImageChange() {},
  onImageChangeStart() {},
  onImageRemoval() {},
  checkedImagesNames: {},
  onAddImageClick() {},
};

export default ImagesTransform;
